import { defineStore } from "pinia"
import { computed, ref, type Ref } from "vue"
import { SocketState } from "@/services/socket/socket-service.ts"

export const useSynchronisationStore = defineStore("synchronisationStore", () => {
  // Current lifecycle state of the synchronisation WebSocket.
  const socketState = ref<SocketState>(SocketState.CONNECTED)

  // Convenience flag derived from `socketState` for callers that only care about connected vs not.
  const socketIsAlive = computed(() => socketState.value === SocketState.CONNECTED)

  // Flag to indicate if messages are unrecoverable (i.e. server message buffer does not contain requested messages)
  const isMessagesUnrecoverable = ref(false)

  const isBulkMessageUnrecoverable = ref(false)

  // Flag to indicate a (potentially large) bulk action is currently being applied. Used to show a
  // "data are being synced" notice to the user, since applying a big backlog can briefly block the UI.
  const isApplyingBulk = ref(false)
  const bulkProgress = ref({ processed: 0, total: 0 })

  // Per-queue desync counters. Real desyncs are handled natively by Centrifugo
  // (positioning + recovery); these remain writable so dev tooling can force the
  // synchronisation-indicator states.
  const queueStates: Ref<{
    [queueName: string]: { desyncCount: number }
  }> = ref({})

  const isDesynced = computed(() => {
    return Object.values(queueStates.value).reduce((acc, curr) => acc + curr.desyncCount, 0) > 0
  })

  return {
    queueStates,
    isDesynced,
    isMessagesUnrecoverable,
    isBulkMessageUnrecoverable,
    isApplyingBulk,
    bulkProgress,
    socketState,
    socketIsAlive
  }
})
